import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { analyticsApi, type AnalyticsSite, type AnalyticsTestResult } from '../lib/salesApi';

/**
 * Admin → Sales data → Google Analytics: which GA4 property is which website,
 * the service account that reads them, a connection test, and the import.
 *
 * The test checks the things that would make conversion wrong rather than
 * just "can we connect": the property's time zone (sales days are New York
 * days), its currency, and whether the site sends ecommerce events at all.
 */

export function AdminAnalytics() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: ['admin-analytics'], queryFn: analyticsApi.get });
  const [sites, setSites] = useState<Record<string, AnalyticsSite>>({});
  const [useSheet, setUseSheet] = useState(true);
  const [keyJson, setKeyJson] = useState('');
  const [itemIdMatches, setItemIdMatches] = useState<'sku' | 'external_id'>('sku');
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [tests, setTests] = useState<AnalyticsTestResult[] | null>(null);
  const data = query.data;

  useEffect(() => {
    if (!data) return;
    setSites(Object.fromEntries(data.settings.sites.map((s) => [s.siteChannelId, s])));
    setUseSheet(data.settings.useSheetServiceAccount);
    setItemIdMatches(data.settings.itemIdMatches);
  }, [data]);

  const refresh = () => void client.invalidateQueries({ queryKey: ['admin-analytics'] });

  const save = useMutation({
    mutationFn: () =>
      analyticsApi.save({
        sites: Object.values(sites).filter((s) => s.propertyId.trim() !== ''),
        useSheetServiceAccount: useSheet,
        itemIdMatches,
        ...(keyJson.trim() ? { serviceAccountJson: keyJson } : {}),
      }),
    onSuccess: () => {
      setKeyJson('');
      setResult({ ok: true, message: 'Saved' });
      refresh();
    },
    onError: (e: Error) => setResult({ ok: false, message: e.message }),
  });
  const test = useMutation({
    mutationFn: analyticsApi.test,
    onSuccess: (r) => setTests(r.results),
    onError: (e: Error) => setResult({ ok: false, message: e.message }),
  });
  const sync = useMutation({
    mutationFn: () => analyticsApi.sync(),
    onSuccess: (r) => {
      const failed = r.results.filter((x) => x.error);
      setResult(
        failed.length > 0
          ? { ok: false, message: failed.map((f) => `${f.channelName}: ${f.error}`).join(' · ') }
          : { ok: true, message: r.results.length ? `Imported ${r.results.map((x) => `${x.channelName} (${x.rows} rows)`).join(', ')}` : 'No property set' },
      );
      refresh();
    },
    onError: (e: Error) => setResult({ ok: false, message: e.message }),
  });
  const restart = useMutation({ mutationFn: analyticsApi.restart, onSuccess: refresh });

  if (query.isLoading) return null;
  if (query.error || !data) return <div className="alert error">{(query.error as Error)?.message ?? 'Could not load Google Analytics settings'}</div>;

  const account = useSheet ? data.settings.sheetServiceAccountEmail : data.settings.serviceAccountEmail || null;
  const setProperty = (channelId: string, propertyId: string) =>
    setSites({ ...sites, [channelId]: { siteChannelId: channelId, propertyId: propertyId.replace(/\D/g, ''), enabled: sites[channelId]?.enabled ?? true } });

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>Google Analytics</h3>
      <p className="small muted" style={{ marginTop: 0 }}>
        Visits from each website's GA4 property, for conversion (website orders ÷ visits), the shopping funnel, where visits come
        from and how often each product is viewed. Imported each morning at 06:20; sales figures never come from GA.
      </p>
      {result && (
        <div className={`alert ${result.ok ? 'ok' : 'error'}`} onClick={() => setResult(null)}>
          {result.message}
        </div>
      )}

      <div className="field">
        <label>Service account</label>
        <label className="inline-check">
          <input type="radio" checked={useSheet} onChange={() => setUseSheet(true)} /> Use the Google Sheet's
        </label>
        <label className="inline-check">
          <input type="radio" checked={!useSheet} onChange={() => setUseSheet(false)} /> Use its own
        </label>
        {!useSheet && (
          <textarea
            rows={3}
            placeholder={data.settings.hasServiceAccountKey ? 'A key is saved — paste a new service-account JSON to replace it' : 'Paste the service-account JSON key'}
            value={keyJson}
            onChange={(e) => setKeyJson(e.target.value)}
            style={{ width: '100%', fontFamily: 'monospace', fontSize: 12, marginTop: 6 }}
          />
        )}
        <span className="small muted">
          {account ? (
            <>
              Add <strong>{account}</strong> as a <strong>Viewer</strong> on each GA4 property (GA Admin → Property access management).
            </>
          ) : useSheet ? (
            'The Google Sheet has no service account saved yet.'
          ) : (
            'No key saved yet.'
          )}
        </span>
      </div>

      <table className="grid compact" style={{ marginTop: 8 }}>
        <thead>
          <tr>
            <th>Website</th>
            <th>GA4 property id</th>
            <th>Import</th>
            <th>Visits from</th>
            <th>Through</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data.websites.map((w) => {
            const site = sites[w.channelId];
            const test = tests?.find((t) => t.siteChannelId === w.channelId);
            return (
              <tr key={w.channelId}>
                <td>{w.name}</td>
                <td>
                  <input
                    inputMode="numeric"
                    placeholder="e.g. 312345678"
                    value={site?.propertyId ?? ''}
                    onChange={(e) => setProperty(w.channelId, e.target.value)}
                    style={{ width: 140 }}
                  />{' '}
                  {site && (
                    <label className="inline-check small">
                      <input type="checkbox" checked={site.enabled} onChange={(e) => setSites({ ...sites, [w.channelId]: { ...site, enabled: e.target.checked } })} /> on
                    </label>
                  )}
                  {test && <TestLine result={test} />}
                </td>
                <td>
                  {w.lastError ? (
                    <span className="badge error" title={w.lastError}>
                      failing
                    </span>
                  ) : w.lastSuccessAt ? (
                    <span className={`badge ${w.backfillStatus === 'complete' ? 'ok' : 'warn'}`}>{w.backfillStatus === 'complete' ? 'up to date' : 'importing history'}</span>
                  ) : (
                    <span className="badge">{w.configured ? 'not run yet' : 'not set'}</span>
                  )}
                  {w.lastError && <div className="small warn-text">{w.lastError}</div>}
                </td>
                <td className="num">{w.historyFrom ?? '—'}</td>
                <td className="num">{w.dataThrough ?? '—'}</td>
                <td>
                  {w.configured && (
                    <button
                      className="link small"
                      disabled={restart.isPending}
                      onClick={() => window.confirm(`Re-import all of ${w.name}'s Google Analytics history?`) && restart.mutate(w.channelId)}
                    >
                      Restart history
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="field" style={{ marginTop: 10 }}>
        <label htmlFor="itemIdMatches">GA item id is the product's</label>
        <select id="itemIdMatches" value={itemIdMatches} onChange={(e) => setItemIdMatches(e.target.value as 'sku' | 'external_id')}>
          <option value="sku">SKU</option>
          <option value="external_id">Site product id (Drupal node / variation id)</option>
        </select>
        <span className="small muted">How product views in GA are matched to products here — whatever the sites send as item_id.</span>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="primary" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button disabled={test.isPending || data.settings.sites.length === 0} onClick={() => test.mutate()}>
          {test.isPending ? 'Testing…' : 'Test connection'}
        </button>
        <button disabled={sync.isPending || data.settings.sites.length === 0} onClick={() => sync.mutate()}>
          {sync.isPending ? 'Importing…' : 'Import now'}
        </button>
      </div>
    </div>
  );
}

function TestLine({ result }: { result: AnalyticsTestResult }) {
  if (!result.ok) return <div className="small warn-text">{result.error}</div>;
  return (
    <div className="small">
      <span className="ok-text">Connected: {result.displayName}</span>
      <span className="muted">
        {' '}
        · {result.timeZone} · {result.currencyCode} · {result.last30?.sessions.toLocaleString()} visits and {result.last30?.purchases} GA purchases in 30 days
      </span>
      {(result.warnings ?? []).map((w) => (
        <div key={w} className="warn-text">
          {w}
        </div>
      ))}
    </div>
  );
}
